import '../App.css';
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';

import FilterBar from '../FilterBar.js'

import { binary_search } from '../util.ts'

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

function GameModePlayed(props) {
  const filterBar = props.filterBarValues;

  const [dataGraph, setDataGraph]= useState(null);
  const [dataGraphBar, setDataGraphBar]= useState(null);

  useEffect(() => {
    var i;
    if(Object.keys(filterBar).length>0) {
      var processingStartTimeFirst = Date.now();

      // Setup game count graph
      var graphDataBarX = ["<1 week", "<1 month", "<3 months", "<6 months", "<1 year", "<3 years", "<5 years", "other"];
      var graphDataBar = [];

      // Setup game amount played graph

      // To be able to bin correctly we need to adjust the start date (oldest date)
      // to a multiple of time granularity. We take the end date (most recent)
      // as "the reference" and substract time granularities until we go pass the 
      // start date.

      // binsPeriod holds the period interval limits. The last one is the end date
      // selected in the interface. The first one is such that the start date
      // selected in the interface is completely captured at the current granularity
      // The first period will be incomplete and should not be displayed.
      var binsPeriodNumber = Math.ceil((filterBar.endDate - filterBar.startDate)/1000/(3600*24*filterBar.timeGranularity)) + 1;
      if(binsPeriodNumber<3) {
        // Period is set to longer than start - end. We force to show at least one period
        binsPeriodNumber = 3;
      };
      const binsPeriod = new Array(binsPeriodNumber).fill(0);
      for(i=0;i<binsPeriodNumber;i++) {
        binsPeriod[binsPeriodNumber-1-i] = Math.round(filterBar.endDate/1000) - i*3600*24*filterBar.timeGranularity;
      };

      const binsPlayerAge = [20*365*24*3600, 5*12*30*24*3600, 3*12*30*24*3600, 12*30*24*3600, 6*30*24*3600, 3*30*24*3600, 30*24*3600, 7*24*3600, 0 ];
      for(i=0;i<binsPlayerAge.length;i++) {
        binsPlayerAge[i] = filterBar.endDate/1000 - binsPlayerAge[i];
      }

      var graphDataX = [];
      var graphData = [];

      for(i=1;i<binsPeriodNumber;i++) {
        const d = new Date(binsPeriod[i]*1000);
        graphDataX.push(d.getFullYear()+"-"+(d.getMonth()+1)+"-"+d.getDate());
      };

      const series = {
        'Casual 2 teams elo (no bots)': {
          'func_filter': (row => props.jsonBattles.is_elo[row]===1 &&
                                 props.jsonBattles.is_teams[row]===1 &&
                                 props.jsonBattles.is_ffa[row]===0 &&
                                 props.jsonBattles.has_bots[row]!==1),
          'color': 'red',
          'values': new Array(binsPeriodNumber).fill(0),
        },
        'Casual 2 teams no elo (no bots)': {
          'func_filter': (row => props.jsonBattles.is_elo[row]===0 &&
                                 props.jsonBattles.is_teams[row]===1 &&
                                 props.jsonBattles.is_ffa[row]===0 &&
                                 props.jsonBattles.has_bots[row]!==1),
          'color': 'coral',
          'values': new Array(binsPeriodNumber).fill(0),
        },
        'Bots (no chicken)': {
          'func_filter': (row => props.jsonBattles.has_bots[row]===1 && props.jsonBattles.is_chicken[row]!==1),
          'color': 'green',
          'values': new Array(binsPeriodNumber).fill(0),
        },
        'Chicken': {
          'func_filter': (row => props.jsonBattles.has_bots[row]===1 && props.jsonBattles.is_chicken[row]===1),
          'color': 'blue',
        },
        'Matchmaker 1v1': {
          'func_filter': (row => props.jsonBattles.is_matchmaker[row]===1 && props.jsonBattles.is_1v1[row]===1),
          'color': 'black',
        },
        'FFA (>3 teams)': {
          'func_filter': (row => props.jsonBattles.is_matchmaker[row]===0 &&
                                 props.jsonBattles.is_ffa[row]===1 &&
                                 props.jsonBattles.has_bots[row]!==1
                               ),
          'color': 'grey',
        },
        'Casual 1v1': {
          'func_filter': (row => props.jsonBattles.is_matchmaker[row]===0 &&
                                 props.jsonBattles.is_teams[row]===0 &&
                                 props.jsonBattles.is_ffa[row]===0 &&
                                 props.jsonBattles.has_bots[row]!==1
                               ),
          'color': 'orange',
        },
      }

      for(const game_type in series) {
        series[game_type].values = new Array(binsPeriodNumber).fill(0);
        series[game_type].valuesBar = new Array(binsPlayerAge.length).fill(0);
      }

      props.jsonPlayers.min = binary_search(props.jsonPlayers.first_battle_sec, (Math.round(Date.now() / 1000) - 3600*24*filterBar.accountAgeRangeDays[1]), false, true);
      props.jsonPlayers.max = binary_search(props.jsonPlayers.first_battle_sec, (Math.round(Date.now() / 1000) - 3600*24*filterBar.accountAgeRangeDays[0]), true, false);

      const player_id_min = props.jsonPlayers.player_id[props.jsonPlayers.min];
      const player_id_max = props.jsonPlayers.player_id[props.jsonPlayers.max];

      props.jsonBattles.min = binary_search(props.jsonBattles.started_sec, binsPeriod[0], false, true);
      props.jsonBattles.max = binary_search(props.jsonBattles.started_sec, binsPeriod[binsPeriodNumber-1], true, false);

      var gamesIds = filterBar.gamesChecked;

      var count_maps = 0;
      var count_battles = 0;
      var count_player = 0;

      for(i=props.jsonBattles.min; i<=props.jsonBattles.max; i++) {
        if(!gamesIds.includes(props.jsonBattles.game_id[i])) {
          continue;
        };

        var map_idx = binary_search(props.jsonMaps.map_id, props.jsonBattles.map_id[i]);

        if(!filterBar.mapTypeAvailableChecked.map((elem, j) =>
            (!elem || filterBar.mapTypeAvailable[j]['value'] === props.jsonMaps[filterBar.mapTypeAvailable[j]['column']][map_idx])).every((x)=>x)) {
              continue;
        }
        var area = props.jsonMaps.size_x[map_idx]*props.jsonMaps.size_y[map_idx];
        if(area<filterBar.mapSize[0] || filterBar.mapSize[1]<area) {
          continue;
        }

        count_maps++;

        var sum_time = 0;

        const battle_player_idx_first = binary_search(props.jsonBattlePlayer.battle_id, props.jsonBattles.battle_id[i], true, false, true);
        var battle_player_idx = battle_player_idx_first;
        var playerSet = new Set();
        var battlePlayerAge = new Array(binsPlayerAge.length).fill(0);
        while(props.jsonBattlePlayer.battle_id[battle_player_idx] == props.jsonBattles.battle_id[i]) {

          // If accountAge filter does not include this player, skip
          if (props.jsonBattlePlayer.player_id[battle_player_idx] < player_id_min ||
            player_id_max < props.jsonBattlePlayer.player_id[battle_player_idx]) {
              battle_player_idx++;
              continue;
          }  

        	sum_time += props.jsonBattlePlayer.playtime_sec[battle_player_idx];

          playerSet.add(props.jsonBattlePlayer.player_id[battle_player_idx]);

          const player_idx = binary_search(props.jsonPlayers.player_id, props.jsonBattlePlayer.player_id[battle_player_idx]);
          const player_bucket_idx = binary_search(binsPlayerAge, props.jsonPlayers.first_battle_sec[player_idx], true, false);
          battlePlayerAge[player_bucket_idx] += 1;
        	battle_player_idx++;
        }
        count_player += playerSet.size;

        const battle_bucket_idx = binary_search(binsPeriod, props.jsonBattles.started_sec[i], false, true);
        for(const game_type in series) {
          if(series[game_type].func_filter(i)) {
              // This battle fits the condition of series game_type
       		    series[game_type].values[battle_bucket_idx] += sum_time

              // Add number of players by account age
              for(var j=0;j<binsPlayerAge.length;j++) {
                series[game_type].valuesBar[j] += battlePlayerAge[j];
              }
          };
        };

        count_battles++;
      };

      // setTotalBattles(count_battles);
      // setTotalMaps(count_maps);
      // setTotalAccounts(count_player);

      for(const game_type in series) {

        // Convert seconds to hours for display
        series[game_type].values = series[game_type].values.map((e)=>e/3600);

        graphData.push({
          'x': graphDataX,
          'y': series[game_type].values.slice(1),
          'mode': 'lines+markers',
          'marker': {
            'color': series[game_type]['color'],
          },
          'name': game_type
        });

        graphDataBar.push({
           x: graphDataBarX,
           y: series[game_type].valuesBar.reverse().slice(1),
           name: game_type,
           marker: {
             color: series[game_type]['color'],
           },
           type: 'bar'
         });
      }
      console.log("INFO: complete time "+(Date.now()-processingStartTimeFirst));
      setDataGraph(graphData);
      setDataGraphBar(graphDataBar);
      return;
    }
  }, [props.filterBarValues, props.jsonBattles])

  var xaxisTitle = "";
  if (filterBar.timeGranularity == 1) {
    xaxisTitle = "Date"
  } else if (filterBar.timeGranularity == 7) {
    xaxisTitle = "Period of one week ending on date";
  } else if (filterBar.timeGranularity == 14) {
    xaxisTitle = "Period of two weeks ending on date";
  } else if (filterBar.timeGranularity == 30) {
    xaxisTitle = "Period of one month (30 days) ending on date";
  }

  const layoutLine = {
    autosize: true,
    title: 'Cummulated play time by game type',
    xaxis: {
      title: xaxisTitle,
      showgrid: false,
      zeroline: false
    },
    yaxis: {
      title: 'Time (player-hours)',
      showline: false
    },
    legend: {
      x: 0.1,
      y: -0.3,
      yanchor: 'top',
      orientation: "h"
    },
  };

  const d = new Date(filterBar.endDate);

  const layoutBar = {
    autosize: true,
    title: 'Number of games versus account age on '+d.getFullYear()+"-"+(d.getMonth()+1)+"-"+d.getDate()+', by game type',
    xaxis: {
      title: 'Account age (intervals are exclusive, ex: <1 year means between 6 months and 1 year)',
      showgrid: false,
      zeroline: false
    },
    yaxis: {
      title: 'Number of games',
      showline: false
    },
    legend: {
      x: 0.1,
      y: -0.3,
      yanchor: 'top',
      orientation: "h"
    },
  };

  return (
    <div>
      <Stack spacing={2}>
        <Item>
          {props.children}
        </Item>
        <Item>
          <Stack direction="row" spacing={1}>
            <Item style={{ width: "50%" }}>
              {dataGraph &&
                <Plot
                  data={dataGraph}
                  useResizeHandler={true}
                  style={{ width: "100%", height: "450px" }}
                  layout={layoutLine}
                  config={ {responsive: true} }
                />
              }
            </Item>
            <Item style={{ width: "50%" }}>
              {dataGraphBar &&
                <Plot
                  data={dataGraphBar}
                  useResizeHandler={true}
                  style={{ width: "100%", height: "450px" }}
                  layout={layoutBar}
                  config={ {responsive: true} }
                />
              }
            </Item>
          </Stack>
        </Item>
      </Stack>
    </div>
  );
}

GameModePlayed.propTypes = {
  jsonBattles: PropTypes.object,
  jsonBattlePlayer: PropTypes.object,
  jsonGames: PropTypes.object,
  jsonMaps: PropTypes.object,
  jsonPlayers: PropTypes.object,
  filterBarValues: PropTypes.object,
};

export default GameModePlayed;
